import moment from "moment";
import {
  CHARGES_TYPE,
  ORDER_TYPE,
  PRODUCT_PRICE_TYPE,
} from "../constant/common";
import {
  PROMOTION_TYPE,
  PROMOTION_TYPE_VARIATION,
} from "../constant/promotions";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getDailyAvailability = (record, now) => {
  if (!record || !record.isAvailableDayActive) return true;

  const effectiveDays = Array.isArray(record.effectiveTypeOptions)
    ? record.effectiveTypeOptions
    : [];
  const currentDay = now.day();
  const currentDayName = now.format("dddd").toUpperCase();
  const isEffectiveDay = effectiveDays.some(
    (day) => Number(day) === currentDay || String(day).toUpperCase() === currentDayName
  );

  if (!isEffectiveDay || !record.effectiveStartTime || !record.effectiveEndTime) {
    return false;
  }

  const start = moment(record.effectiveStartTime)
    .year(now.year())
    .month(now.month())
    .date(now.date());
  const end = moment(record.effectiveEndTime)
    .year(now.year())
    .month(now.month())
    .date(now.date());

  return now.isSameOrAfter(start) && now.isBefore(end);
};

const isCategoryAvailable = ({
  category,
  orderType,
  outletSectionId,
  now,
}) => {
  if (!category || category.isActive === false) return false;
  if (category.hideInOrderTypes?.includes(orderType)) return false;
  if (!getDailyAvailability(category, now)) return false;

  return !(
    orderType === ORDER_TYPE.DINEIN &&
    category.hideOutletSectionIdList?.includes(outletSectionId)
  );
};

export const isPromotionItemAvailable = (
  item,
  {
    categoriesById = {},
    orderType,
    outletSectionId,
    selectedOutletId,
    now = moment(),
  } = {}
) => {
  if (!item?.uniqueId || item.isFakeItem || item.isActive !== true) return false;
  if (selectedOutletId && item.outletId && item.outletId !== selectedOutletId) {
    return false;
  }
  if (
    item.priceType !== undefined &&
    item.priceType !== PRODUCT_PRICE_TYPE.FIXED &&
    item.priceType !== PRODUCT_PRICE_TYPE.UNIT
  ) {
    return false;
  }
  if (item.hideInOrderTypes?.includes(orderType)) return false;
  if (item.isOnlineMenu === false || !getDailyAvailability(item, now)) {
    return false;
  }
  if (item.isStockCountActive === true && !(Number(item.stockCount) > 0)) {
    return false;
  }
  if (
    item.isStockCountActive !== undefined &&
    item.isStockCountActive !== false &&
    item.stockCount !== undefined &&
    item.toSellIgnoreStock === false
  ) {
    return false;
  }

  const category = categoriesById[item.categoryId];
  if (
    !isCategoryAvailable({ category, orderType, outletSectionId, now }) ||
    item.excludePromoVoucher ||
    category.excludePromoVoucher
  ) {
    return false;
  }

  return true;
};

const normalizeTargets = (targets) => {
  if (!Array.isArray(targets)) return [];

  return targets.flatMap((target) => {
    if (typeof target === "string" || typeof target === "number") {
      return [String(target)];
    }
    if (!target || typeof target !== "object") return [];
    return [target.sku, target.name, target.uniqueId]
      .filter(Boolean)
      .map(String);
  });
};

const getCriteriaTargets = (promotion, criteria) => {
  const isBuyGet =
    promotion.promotionType === PROMOTION_TYPE.BUY_A_GET_B_FOR_FREE;
  return {
    variation: isBuyGet ? criteria.buyVariation : criteria.variation,
    targets: normalizeTargets(
      isBuyGet
        ? criteria.buyVariationItemsSku || criteria.buyVariationItems
        : criteria.variationItemsSku || criteria.variationItems
    ),
  };
};

const criteriaMatchesItem = (promotion, criteria, item, category) => {
  const { variation, targets } = getCriteriaTargets(promotion, criteria);

  if (variation === PROMOTION_TYPE_VARIATION.SPECIFIC_PRODUCTS) {
    return targets.includes(String(item.sku));
  }
  if (variation === PROMOTION_TYPE_VARIATION.PRODUCT_OF_CATEGORY) {
    return Boolean(category?.name && targets.includes(String(category.name)));
  }

  return false;
};

const PROMOTION_TYPES_WITH_ITEM_PRICING = new Set([
  PROMOTION_TYPE.OVERRIDE_EXISTING_PRICE,
  PROMOTION_TYPE.TAKE_AMOUNT_OFF,
  PROMOTION_TYPE.TAKE_PERCENTAGE_OFF,
  PROMOTION_TYPE.BUY_A_GET_B_FOR_FREE,
]);

const hasPromotionImage = (promotion) =>
  typeof promotion?.image === "string" && promotion.image.trim() !== "";

const getItemExtraPrice = (item, selectedOutlet, orderType) => {
  const itemPrice = toNumber(item.price) || 0;
  let extraPrice = 0;

  if (orderType === ORDER_TYPE.DELIVERY) {
    extraPrice = toNumber(selectedOutlet?.deliveryPrice) || 0;
    if (item.deliveryChargesActive) {
      extraPrice = toNumber(item.deliveryCharges) || 0;
      if (
        extraPrice &&
        item.deliveryChargesType === CHARGES_TYPE.PERCENTAGE_BASED
      ) {
        extraPrice = (itemPrice * extraPrice) / 100;
      }
    }
  } else if (orderType === ORDER_TYPE.PICKUP) {
    extraPrice = toNumber(item.pickUpCharges) || 0;
    if (
      extraPrice &&
      item.pickUpChargesType === CHARGES_TYPE.PERCENTAGE_BASED
    ) {
      extraPrice = (itemPrice * extraPrice) / 100;
    }
    if (!item.pickUpChargesActive) extraPrice = 0;
  }

  return extraPrice;
};

const getOfferConditions = (promotion, criteria) => {
  const conditions = [];
  const quantityMin = toNumber(criteria.quantityMin ?? criteria.minQuantity);
  const criteriaSpend = toNumber(criteria.priceMin);
  const promotionSpend = toNumber(promotion.minSpend);

  if (quantityMin && quantityMin > 1) {
    conditions.push(`Buy ${quantityMin} or more`);
  }
  if (criteriaSpend && criteriaSpend > 0) {
    conditions.push(`Minimum qualifying spend RM${criteriaSpend.toFixed(2)}`);
  }
  if (promotionSpend && promotionSpend > 0 && promotionSpend !== criteriaSpend) {
    conditions.push(`Minimum order RM${promotionSpend.toFixed(2)}`);
  }
  if ((toNumber(criteria.qtyRp) || 0) > 0) {
    conditions.push("Qualifying purchase required");
  }
  if (promotion.usePromoCode && promotion.promoCode) {
    conditions.push(`Use code ${promotion.promoCode}`);
  }

  return conditions;
};

export const getPromotionItemOffer = ({
  promotion,
  criteria = {},
  item,
  selectedOutlet,
  orderType,
}) => {
  const itemPrice = toNumber(item.price) || 0;
  const extraPrice = getItemExtraPrice(item, selectedOutlet, orderType);
  const originalPrice = itemPrice + extraPrice;
  const conditions = getOfferConditions(promotion, criteria);
  const requiresMultipleItems =
    (toNumber(criteria.quantityMin ?? criteria.minQuantity) || 0) > 1;
  const requiresRelatedPurchase = (toNumber(criteria.qtyRp) || 0) > 0;
  let discountedPrice = null;
  let label = promotion.campaignDescription || "Promotion applies";

  if (promotion.promotionType === PROMOTION_TYPE.OVERRIDE_EXISTING_PRICE) {
    const overridePrice = toNumber(criteria.priceBeforeTax);
    if (overridePrice !== null) {
      discountedPrice = overridePrice + extraPrice;
      label = `Promo price RM${discountedPrice.toFixed(2)}`;
    }
  } else if (promotion.promotionType === PROMOTION_TYPE.TAKE_AMOUNT_OFF) {
    const amountOff = toNumber(criteria.amountOff);
    if (amountOff !== null) {
      label = `RM${amountOff.toFixed(2)} off`;
      if (
        !promotion.applyAllMenu &&
        !requiresMultipleItems &&
        !requiresRelatedPurchase
      ) {
        const minimumPrice = toNumber(criteria.minPriceToDiscounted) || 0;
        discountedPrice = Math.max(originalPrice - amountOff, minimumPrice, 0);
      }
    }
  } else if (promotion.promotionType === PROMOTION_TYPE.TAKE_PERCENTAGE_OFF) {
    const percentageOff = toNumber(criteria.percentageOff);
    if (percentageOff !== null) {
      label = `${percentageOff.toFixed(0)}% off`;
      if (
        !promotion.applyAllMenu &&
        !requiresMultipleItems &&
        !requiresRelatedPurchase
      ) {
        const minimumPrice = toNumber(criteria.minPriceToDiscounted) || 0;
        discountedPrice = Math.max(
          originalPrice * (1 - percentageOff / 100),
          minimumPrice,
          0
        );
      }
    }
  } else if (
    promotion.promotionType === PROMOTION_TYPE.BUY_A_GET_B_FOR_FREE
  ) {
    const buyAmount = toNumber(criteria.buyAmount) || 1;
    const getAmount = toNumber(criteria.getAmount) || 1;
    const getPrice = toNumber(criteria.getPrice);
    label =
      getPrice === null || getPrice === 0
        ? `Buy ${buyAmount}, get ${getAmount} free`
        : `Buy ${buyAmount}, get ${getAmount} for RM${getPrice.toFixed(2)}`;
  }

  if (discountedPrice !== null && discountedPrice >= originalPrice) {
    discountedPrice = null;
  }

  return {
    conditions,
    discountedPrice,
    isConditional: conditions.length > 0 || promotion.applyAllMenu === true,
    label,
    originalPrice,
  };
};

export const getPromotionItems = ({
  promotion,
  items = [],
  categoriesById = {},
  selectedOutlet,
  orderType,
  outletSectionId,
  now = moment(),
}) => {
  if (!promotion) return [];

  const criteriaList = Array.isArray(promotion.criteriaList)
    ? promotion.criteriaList
    : [];
  const matchingItems = new Map();

  for (const item of items) {
    if (
      !isPromotionItemAvailable(item, {
        categoriesById,
        orderType,
        outletSectionId,
        selectedOutletId: selectedOutlet?.uniqueId,
        now,
      })
    ) {
      continue;
    }

    const category = categoriesById[item.categoryId];
    const criteria = criteriaList.find((entry) =>
      criteriaMatchesItem(promotion, entry, item, category)
    );

    if (!criteria) continue;

    matchingItems.set(item.uniqueId, {
      category,
      criteria,
      item,
      offer: getPromotionItemOffer({
        promotion,
        criteria,
        item,
        selectedOutlet,
        orderType,
      }),
    });
  }

  return Array.from(matchingItems.values()).sort((a, b) => {
    const orderA = toNumber(a.item.orderIndex);
    const orderB = toNumber(b.item.orderIndex);
    if (orderA !== null || orderB !== null) {
      return (orderA ?? Number.MAX_SAFE_INTEGER) -
        (orderB ?? Number.MAX_SAFE_INTEGER);
    }
    return String(a.item.name || "").localeCompare(String(b.item.name || ""));
  });
};

export const getPromotionSlides = ({
  promotions = [],
  items = [],
  categoriesById = {},
  selectedOutlet,
  orderType,
  outletSectionId,
  now = moment(),
}) => {
  if (!selectedOutlet?.uniqueId) return [];

  return promotions.flatMap((promotion) => {
    if (
      !promotion ||
      promotion.displayOnLanding !== true ||
      !PROMOTION_TYPES_WITH_ITEM_PRICING.has(promotion.promotionType) ||
      promotion.isActive === false ||
      promotion.deletedAt != null ||
      !hasPromotionImage(promotion) ||
      (Array.isArray(promotion.outletIdList) &&
        !promotion.outletIdList.includes(selectedOutlet.uniqueId))
    ) {
      return [];
    }

    const promotionItems = getPromotionItems({
      promotion,
      items,
      categoriesById,
      selectedOutlet,
      orderType,
      outletSectionId,
      now,
    });

    return promotionItems.length > 0
      ? [{ promotion, items: promotionItems }]
      : [];
  });
};
